import { useCallback, useEffect, useMemo } from 'react'
import { ReservedResourcePhase } from 'src/api/api.generated'
import { useHotelDate } from 'src/app/hooks/context/context.hook'
import { compareDates } from 'src/utils/date'
import { GuestCheckinCheckoutAction } from '../types/guests.types'

export enum FrontendPhase {
  NotYetArrived,
  Arriving,
  Home,
  NoShow,
  Departing,
  Departed,
}

export type NextPhase = {
  /** the frontend phase to show */
  frontendPhase: FrontendPhase
  /** the action to do */
  action: GuestCheckinCheckoutAction
}

export const useFrontendPhase = (start?: Date | null, end?: Date | null, rrp?: ReservedResourcePhase | null) => {
  const hotelDate = useHotelDate()

  /** calculate current frontend phase */
  const getCurrentPhase = useCallback(
    (start: Date | null, end: Date | null, rrp: ReservedResourcePhase | null = 'RESERVATION') => {
      const compareStart = start ? compareDates(start, hotelDate) : 1
      const compareEnd = end ? compareDates(end, hotelDate) : 1
      switch (rrp) {
        case 'RESERVATION':
          if (compareStart === 0) return FrontendPhase.Arriving
          if (compareStart < 0) return FrontendPhase.NoShow
          if (compareStart > 0) return FrontendPhase.NotYetArrived
          break
        case 'CHECKIN':
          if (compareEnd >= 0) return FrontendPhase.Home
          if (compareEnd < 0) return FrontendPhase.Departing
          break
        case 'CHECKOUT':
          return FrontendPhase.Departed
      }
      return FrontendPhase.NotYetArrived
    },
    [hotelDate],
  )

  /** current frontend phase */
  const currentPhase = useMemo(() => {
    return getCurrentPhase(start ?? null, end ?? null, rrp ?? null)
  }, [start, end, rrp, getCurrentPhase])

  /** calculate next phases allowed from current reserved resouce or guest occupance phase.*/
  const getNextPhaseTransitionsAllowed = (fePhase: FrontendPhase): NextPhase[] => {
    switch (fePhase) {
      case FrontendPhase.NotYetArrived:
        return [{ frontendPhase: FrontendPhase.Home, action: 'DO_CHECKIN' }]
      case FrontendPhase.Arriving:
        return [{ frontendPhase: FrontendPhase.Home, action: 'DO_CHECKIN' }]
      case FrontendPhase.Home:
        return [
          { frontendPhase: FrontendPhase.NotYetArrived, action: 'UNDO_CHECKIN' },
          { frontendPhase: FrontendPhase.Departed, action: 'DO_CHECKOUT' },
        ]
      case FrontendPhase.NoShow:
        return [{ frontendPhase: FrontendPhase.Home, action: 'DO_CHECKIN' }]
      case FrontendPhase.Departing:
        return [
          { frontendPhase: FrontendPhase.NotYetArrived, action: 'UNDO_CHECKIN' },
          { frontendPhase: FrontendPhase.Departed, action: 'DO_CHECKOUT' },
        ]
      case FrontendPhase.Departed:
        return [{ frontendPhase: FrontendPhase.Home, action: 'UNDO_CHECKOUT' }]
    }
  }

  /** next phases allowed from current reserved resource or guest occupancy phase.*/
  const nextPhaseTransitionsAllowed = useMemo(() => {
    return getNextPhaseTransitionsAllowed(currentPhase)
  }, [currentPhase, getNextPhaseTransitionsAllowed])

  return { currentPhase, getCurrentPhase, nextPhaseTransitionsAllowed, getNextPhaseTransitionsAllowed }
}
